/**
 * The platform's Excel export: POST `{base}` (the platform API) with the
 * `GenericExcelExportService` endpoint - the server reads the entity's rows that
 * match `filters` and answers with the .xlsx as base64:
 *
 *   responseList[0].excelExport[0] = { status, fileName, contentType, contentBase64, rowCount }
 */

/** One active dashboard selection: a column and the value(s) picked for it. */
export interface ExcelExportFilter {
  field: string;
  values: readonly string[];
}

export interface ExcelExportOptions {
  entity: string;
  /** The columns exported (attributesList), in order; empty = every column of the entity. */
  columns?: readonly string[];
  /** Applied with AND; one value is `=`, several are `in`. */
  filters?: readonly ExcelExportFilter[];
  /** Without extension. Default: the entity name. */
  fileName?: string;
  sheetName?: string;
}

export interface ExcelExportFile {
  fileName: string;
  blob: Blob;
  rowCount: number | null;
}

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The export API, relative to the self-service base (= the platform API itself). */
export const EXCEL_EXPORT_DEFAULT_URL = '/spriced/platform';

/** The default request body, as an editable template: {entity} {columns} {filters} {fileName} {sheetName}. */
export const EXCEL_EXPORT_DEFAULT_BODY = `{
  "requestData": {
    "entity": "{entity}",
    "attributesList": {columns},
    "filters": {filters},
    "fileName": "{fileName}",
    "sheetName": "{sheetName}"
  },
  "responseList": [],
  "entity": "{entity}",
  "operation": "read",
  "workflowId": "UI",
  "endpoints": [{ "functionName": "GenericExcelExportService", "version": "1.0" }],
  "communicationType": "rest",
  "version": "1.0"
}`;

/** The request body for one export. Every row is in scope (`uuid is not null`), narrowed by the filters. */
export function buildExcelExportRequest(options: ExcelExportOptions): Record<string, unknown> {
  const entity = options.entity.trim();
  return {
    requestData: {
      entity,
      ...(options.columns?.length ? { attributesList: [...options.columns] } : {}),
      filters: excelExportFilters(options.filters),
      fileName: (options.fileName || entity).trim(),
      sheetName: (options.sheetName || 'Sheet1').trim(),
    },
    responseList: [],
    entity,
    operation: 'read',
    workflowId: 'UI',
    endpoints: [{ functionName: 'GenericExcelExportService', version: '1.0' }],
    communicationType: 'rest',
    version: '1.0',
  };
}

/** The `filters` array: one condition per selection (= one value, in several), AND-joined, then `uuid is not null`. */
export function excelExportFilters(selections: readonly ExcelExportFilter[] | undefined): Array<Record<string, unknown>> {
  const conditions = (selections ?? [])
    .map((filter) => ({ field: filter.field.trim(), values: filter.values.map(String).filter((value) => value !== '') }))
    .filter((filter) => filter.field && filter.values.length)
    .map((filter) => ({
      attributeName: filter.field,
      operator: filter.values.length > 1 ? 'in' : '=',
      attributeValue: filter.values.length > 1 ? filter.values : filter.values[0],
      attributeType: 'string',
    }));
  // Only with nothing to filter on: "every row" still needs one condition.
  if (!conditions.length) conditions.push({ attributeName: 'uuid', operator: 'is not', attributeValue: 'null', attributeType: 'string' });
  return conditions.map((condition, index) => ({
    ...condition,
    // Each condition is joined to the NEXT one; the last joins nothing.
    logicalOperator: index < conditions.length - 1 ? 'AND' : '',
    beginScope: '',
    endScope: '',
  }));
}

/** The exported file from the response, or the reason there is none. */
export function excelExportFileOf(response: unknown): ExcelExportFile | { error: string } {
  const record = (response ?? {}) as Record<string, unknown>;
  const list = Array.isArray(record['responseList']) ? (record['responseList'] as Array<Record<string, unknown>>) : [];
  const exports = list.flatMap((entry) => (Array.isArray(entry?.['excelExport']) ? (entry['excelExport'] as Array<Record<string, unknown>>) : []));
  const file = exports[0];
  if (!file) return { error: String(record['error'] || 'The server returned no file') };
  if (String(file['status'] ?? '').toUpperCase() !== 'SUCCESS' || typeof file['contentBase64'] !== 'string') {
    return { error: String(file['message'] || file['status'] || 'The export failed') };
  }
  const binary = atob(file['contentBase64'] as string);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  const name = String(file['fileName'] || 'export.xlsx');
  return {
    fileName: /\.xlsx$/i.test(name) ? name : `${name}.xlsx`,
    blob: new Blob([bytes], { type: String(file['contentType'] || XLSX_TYPE) }),
    rowCount: typeof file['rowCount'] === 'number' ? (file['rowCount'] as number) : null,
  };
}
